import { TextAreaWithLabel } from '@adotel/components'
import { useMutation } from '@apollo/client'
import { Box, Button, Typography } from '@material-ui/core'
import { useState } from 'react'
import { toast } from 'react-toastify'

import * as notificationGraphql from '../../graphql/notifications'
import ProviderConfigForm from './ProviderConfigForm'
import { ResourcePanel } from './ResourcePanel'
import { bindNotificationScope } from './notificationAccess'
import { PROVIDER_CONFIG_COLUMNS } from './resourceColumns'
import { idempotencyKey, t } from './shared'

export function ProviderPanel({
  canManage,
  globalScope,
  currentScope
}: {
  canManage: boolean
  globalScope: boolean
  currentScope?: { type: string; code: string }
}) {
  const [policyDraft, setPolicyDraft] = useState(
    '{\n  "entity": { "type": "PROVIDER", "code": "" },\n  "channel": "EMAIL",\n  "mode": "REQUIRE_OWN"\n}'
  )
  const [setPolicy, { loading: savingPolicy }] = useMutation(
    notificationGraphql.SET_NOTIFICATION_PROVIDER_POLICY_MUTATION
  )
  return (
    <Box>
      <ResourcePanel
        title="Provider configurations"
        columns={PROVIDER_CONFIG_COLUMNS}
        query={notificationGraphql.NOTIFICATION_PROVIDER_CONFIGS_QUERY}
        resultKey="notificationProviderConfigs"
        listKey="configs"
        queryVariables={
          !globalScope && currentScope ? { scope: currentScope } : {}
        }
        canManage={canManage}
        mutation={
          notificationGraphql.UPSERT_NOTIFICATION_PROVIDER_CONFIG_MUTATION
        }
        // Editing is on again. It was off because the list returns a
        // credential fingerprint rather than the credentials, so a
        // round-tripped row submitted an empty credentials object and the
        // service encrypted that over the stored secret. The service now reads
        // an omitted object on an update as "leave what is stored", and the
        // form asks before replacing them.
        renderEditor={(submit, saving, editing) => (
          <ProviderConfigForm
            initial={editing}
            saving={saving}
            onSubmit={submit}
          />
        )}
        variableName="config"
        mutationVariables={(value) => {
          const { credentials = {} } = value
          // An allowlist, not a spread. A prefilled row carries the query's
          // output fields — `credentialFingerprint` and `lastVerifiedAt` —
          // and NotificationProviderConfigInput has neither, so spreading the
          // row makes the server reject the mutation on unknown input fields.
          // Naming the fields also means a future query addition cannot
          // silently break saving.
          const config: Record<string, unknown> = {
            id: value.id,
            channel: value.channel,
            providerKey: value.providerKey,
            scope: value.scope,
            settings: value.settings ?? {},
            active: Boolean(value.active)
          }
          return {
            config:
              !globalScope && currentScope
                ? bindNotificationScope(config, currentScope, 'scope')
                : config,
            credentials,
            idempotencyKey: idempotencyKey()
          }
        }}
        actionMutation={
          notificationGraphql.VERIFY_NOTIFICATION_PROVIDER_CONFIG_MUTATION
        }
        actionLabel="Verify"
      />
      {canManage && (
        <Box mt={3}>
          <Typography variant="subtitle1">
            {t('Provider ownership policy')}
          </Typography>
          <TextAreaWithLabel
            fullWidth
            label={t('Provider policy JSON')}
            noHelperText
            rows={6}
            value={policyDraft}
            onChange={(event) => setPolicyDraft(event.currentTarget.value)}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={savingPolicy}
            onClick={async () => {
              try {
                await setPolicy({
                  variables: {
                    policy:
                      !globalScope && currentScope
                        ? bindNotificationScope(
                            JSON.parse(policyDraft),
                            currentScope,
                            'entity'
                          )
                        : JSON.parse(policyDraft),
                    idempotencyKey: idempotencyKey()
                  }
                })
                toast.success(t('Provider policy saved.'))
              } catch (policyError) {
                toast.error(
                  policyError instanceof SyntaxError
                    ? t('Invalid JSON. Check the form and try again.')
                    : t('Unable to save provider policy.')
                )
              }
            }}>
            {savingPolicy ? t('Saving…') : t('Save policy')}
          </Button>
        </Box>
      )}
    </Box>
  )
}
